/**
 * printer-router — Résolution d'imprimante et routing de bas niveau.
 *
 * Ce module orchestre le transport physique : il décide quel adapter appeler
 * selon les bridges disponibles (AIDL → Cloud Printer → BT → Network) et la
 * configuration DB. Il ne charge PAS de données depuis sales/sale_items.
 *
 * Contenu :
 *   - findPrinter()        — résolution 4-étapes depuis tenant_printers
 *   - listTenantPrinters() — liste imprimantes actives d'un tenant
 *   - loadTicketConfig()   — chargement configuration ticket depuis DB
 *   - printReceipt()       — route ticket → AIDL → Cloud → BT → Network
 *   - printZReport()       — route rapport Z → même pipeline
 *   - printKitchen()       — bon cuisine vers imprimante configurée
 *   - openDrawer()         — ouverture tiroir-caisse toutes routes
 *   - openDrawerForRegister() — alias public
 *
 * DETTE (Phase D) : les appels supabase.* doivent migrer vers src/api/printing/
 * (invariant 4.B.2). Le DEBT marker est sur chaque import supabase direct.
 */

import { supabase } from '@/integrations/supabase/client'; // DEBT(Phase-D): legacy — frontière publique = src/api/printing/ // eslint-disable-line
import { devLog } from '@/lib/logger';
import { EDITOR } from '@/config/editor';
import type { PrinterConfig, TicketData } from './escpos-types';
import { DRAWER_KICK_AIDL, DRAWER_KICK_ESCPOS } from './escpos-types';
import {
  buildEscPosReceipt,
  buildEscPosKitchen,
  buildEscPosZReport,
  type KitchenData,
  type ZReportData,
} from './escpos-formatter';
import {
  sendViaBluetooth,
  sendViaAidl,
  sendViaAidlZReport,
  sendViaCloudPrinter,
  sendViaCloudPrinterZReport,
  sendViaNetwork,
} from './printer-adapters';
// Routing réseau adaptatif (natif socket TCP / web Edge Function) — voir
// printViaNetworkOrCloud dans network-printer-adapter.ts.
import { printViaNetworkOrCloud } from './network-printer-adapter';

export type { PrinterConfig, TicketData } from './escpos-types';
export type { ZReportData, KitchenData } from './escpos-formatter';

// ── Résolution d'imprimante ──────────────────────────────────────────────────

/**
 * Résout l'imprimante à utiliser selon une logique 5-étapes :
 *   0. Override explicite (printerId fourni)
 *   1. Match par fonction métier (printerFunction fournie : cuisine, bar, …)
 *   2. Exact match register + type
 *   3. Défaut tenant pour ce type
 *   4. N'importe quel défaut tenant
 *
 * printerFunction est un filtre opportuniste : si aucune imprimante ne porte
 * la fonction demandée, la résolution historique continue (jamais d'échec
 * d'impression parce qu'une fonction n'est pas encore assignée).
 */
export async function findPrinter(
  tenantId: string,
  registerId?: string,
  printerType: string = 'receipt',
  printerId?: string,
  printerFunction?: string,
): Promise<PrinterConfig | null> {
  if (printerId) {
    const { data } = await supabase
      .from('tenant_printers')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('id', printerId)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (data) return data as unknown as PrinterConfig;
  }

  if (printerFunction) {
    const { data } = await supabase
      .from('tenant_printers')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('printer_function', printerFunction)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .limit(1)
      .maybeSingle();
    // Exception 4.B.4 commentée : cast Row→PrinterConfig homogène aux 5 casts
    // legacy du module (DEBT Phase-D, cf. en-tête — apurement = mapper typé
    // dans src/api/printing/ lors de la migration 4.B.2 de ce fichier).
    if (data) return data as unknown as PrinterConfig;
  }

  if (registerId) {
    const { data } = await supabase
      .from('tenant_printers')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('register_id', registerId)
      .eq('printer_type', printerType)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (data) return data as unknown as PrinterConfig;
  }

  const { data: typeDefault } = await supabase
    .from('tenant_printers')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('printer_type', printerType)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (typeDefault) return typeDefault as unknown as PrinterConfig;

  const { data: anyDefault } = await supabase
    .from('tenant_printers')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
    .limit(1)
    .maybeSingle();
  return anyDefault ? (anyDefault as unknown as PrinterConfig) : null;
}

/**
 * Liste toutes les imprimantes actives d'un tenant, filtrées par types.
 * Utilisé par le picker manuel (KOT) pour proposer les cibles cuisine/bar.
 */
export async function listTenantPrinters(
  tenantId: string,
  types?: string[],
): Promise<PrinterConfig[]> {
  let query = supabase
    .from('tenant_printers')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
    .order('name', { ascending: true });

  if (types && types.length > 0) {
    query = query.in('printer_type', types);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as PrinterConfig[];
}

// ── Ticket Config ────────────────────────────────────────────────────────────

/** Charge la configuration ticket depuis la DB, avec fallback sur les défauts NF525. */
export async function loadTicketConfig(
  ticketType: string = 'caisse',
  tenantId?: string,
): Promise<TicketData['config']> {
  const defaults: TicketData['config'] = {
    show_date: true,
    show_heure: true,
    show_vendeur: true,
    show_num_ticket: true,
    show_client: false,
    show_ref_article: false,
    show_detail_tva: true,
    show_tva_par_taux: true,
    show_mode_reglement: true,
    show_hash_fiscal: true,
    show_merci: true,
    show_code_barres_ticket: false,
    show_qr_code: false,
    merci_text: 'Merci de votre visite !',
    footer_text: '',
    footer_legal: '',
    // Mentions légales par défaut (auto-certification art. 286 I-3° bis CGI +
    // loi anti-gaspillage AGEC) : présentes même sans config ticket_config DB.
    // JAMAIS "certifié NF525" tant qu'INFOCERT non signé (auto-cert only).
    mention_nf525: EDITOR.nf525Mention,
    mention_agec: 'Ticket imprimé à votre demande — Loi du 10 février 2020 article 49',
    qr_code_content: '',
    police_size: 20,
    largeur_papier: 80,
  };

  try {
    let query = supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ticket_config pas encore dans types.ts
      .from('ticket_config' as any)
      .select('*')
      .eq('ticket_type', ticketType);
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data } = await query.limit(1);
    const cfg = Array.isArray(data) ? data[0] : data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ticket_config pas encore dans types.ts (DEBT Phase-D)
    if (cfg) return { ...defaults, ...(cfg as any) };
  } catch {
    /* retourne les défauts */
  }
  return defaults;
}

// ── printReceipt ─────────────────────────────────────────────────────────────

/**
 * Imprime un ticket de caisse en routant selon les bridges disponibles :
 *   PRIORITÉ 1    : AIDL intégrée (Sunmi V1/V2/D3 Mini)
 *   PRIORITÉ 1bis : Cloud Printer (D3 Pro via PrinterX)
 *   PRIORITÉ 2    : Imprimante configurée (BT, réseau, USB)
 */
export async function printReceipt(
  tenantId: string,
  registerId: string | undefined,
  ticketData: TicketData,
  printerId?: string,
): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL non typé
  const w = window as any;
  const aidlBridge = w.SunmiPrinter || w.SunmiInnerPrinter;

  // Résoudre l'imprimante configurée AVANT de tenter l'AIDL/Cloud. Si une
  // imprimante BT/réseau est explicitement configurée, on NE tente PAS l'AIDL
  // interne d'abord : sur un device Sunmi avec imprimante externe, sendViaAidl
  // attend ~8s (CONNECT_TIMEOUT) une connexion interne qui ne vient jamais avant
  // de retomber sur le BT → délai ~14s observé (La Mie Vaudaise / CloudPrint).
  const printer = await findPrinter(tenantId, registerId, 'receipt', printerId);
  const preferConfigured = ['bluetooth', 'network'].includes(printer?.connection_type ?? '');

  if (aidlBridge && !preferConfigured) {
    devLog('[PrintService] Receipt → AIDL intégrée');
    const ok = await sendViaAidl(ticketData);
    if (ok) return true;
    console.warn('[PrintService] AIDL failed, trying configured printer...');
  }

  const pxBridge = w.SunmiPrinterX;
  if (!aidlBridge && !preferConfigured && pxBridge?.isCloudPrinterAvailable?.()) {
    devLog('[PrintService] Receipt → Cloud Printer (PrinterX)');
    const ok = await sendViaCloudPrinter(ticketData);
    if (ok) return true;
    console.warn('[PrintService] Cloud Printer failed, trying configured printer...');
  }

  if (!printer) throw new Error("Aucune imprimante configurée et pas d'AIDL");

  devLog(`[PrintService] Receipt → ${printer.name} (${printer.connection_type})`);

  switch (printer.connection_type) {
    case 'sunmi':
      return sendViaAidl(ticketData);
    case 'bluetooth':
      if (!printer.bt_mac_address) throw new Error('Adresse MAC manquante');
      return sendViaBluetooth(printer.bt_mac_address, buildEscPosReceipt(ticketData));
    case 'network':
      if (!printer.ip_address) throw new Error('Adresse IP manquante');
      return printViaNetworkOrCloud(
        printer.ip_address,
        printer.port || 9100,
        buildEscPosReceipt(ticketData),
      );
    default:
      throw new Error('Type de connexion non supporté: ' + printer.connection_type);
  }
}

// ── printZReport ─────────────────────────────────────────────────────────────

/**
 * Imprime un rapport Z (clôture session NF525) ou X (rapport informatif).
 * Route : AIDL → Cloud Printer → BT → Network.
 *
 * Fix 2026-04-30 : avant ce refactor, usePrinter.ts:printZReport appelait
 * directement l'AIDL inline sans passer par PrintService.
 */
export async function printZReport(
  tenantId: string,
  registerId: string | undefined,
  closureData: ZReportData,
): Promise<{
  ok: boolean;
  route: string;
  printerName?: string;
  printerId?: string;
  printerMac?: string;
  bytes?: number;
  error?: string;
}> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL non typé
  const w = window as any;
  const aidlBridge = w.SunmiPrinter || w.SunmiInnerPrinter;
  if (aidlBridge) {
    devLog('[PrintService] Z report → AIDL intégrée');
    const ok = await sendViaAidlZReport(closureData);
    if (ok) return { ok: true, route: 'aidl' };
    console.warn('[PrintService] AIDL Z failed, trying Cloud Printer...');
  }

  const pxBridge = w.SunmiPrinterX;
  if (pxBridge?.isCloudPrinterAvailable?.()) {
    devLog('[PrintService] Z report → Cloud Printer (PrinterX)');
    const ok = await sendViaCloudPrinterZReport(closureData);
    if (ok) return { ok: true, route: 'cloud_printer' };
    console.warn('[PrintService] Cloud Printer Z failed, trying configured printer...');
  }

  const printer = await findPrinter(tenantId, registerId, 'receipt');
  if (!printer) {
    return {
      ok: false,
      route: 'unknown',
      error: "Aucune imprimante configurée et pas d'AIDL/CloudPrinter",
    };
  }

  const bytes = buildEscPosZReport(closureData);
  devLog(
    `[PrintService] Z report → ${printer.name} (${printer.connection_type}, ${bytes.length} bytes)`,
  );

  try {
    let routedOk = false;
    switch (printer.connection_type) {
      case 'sunmi':
        routedOk = await sendViaAidlZReport(closureData);
        return {
          ok: routedOk,
          route: 'sunmi_legacy',
          printerName: printer.name,
          printerId: (printer as { id?: string }).id,
          bytes: bytes.length,
        };
      case 'bluetooth':
        if (!printer.bt_mac_address) throw new Error('Adresse MAC manquante');
        routedOk = await sendViaBluetooth(printer.bt_mac_address, bytes);
        return {
          ok: routedOk,
          route: 'bluetooth',
          printerName: printer.name,
          printerId: (printer as { id?: string }).id,
          printerMac: printer.bt_mac_address,
          bytes: bytes.length,
        };
      case 'network':
        if (!printer.ip_address) throw new Error('Adresse IP manquante');
        routedOk = await printViaNetworkOrCloud(printer.ip_address, printer.port || 9100, bytes);
        return {
          ok: routedOk,
          route: 'network',
          printerName: printer.name,
          printerId: (printer as { id?: string }).id,
          bytes: bytes.length,
        };
      default:
        return {
          ok: false,
          route: 'unknown',
          error: 'Type de connexion non supporté: ' + printer.connection_type,
          printerName: printer.name,
        };
    }
  } catch (e) {
    return {
      ok: false,
      route: printer.connection_type,
      error: e instanceof Error ? e.message : String(e),
      printerName: printer.name,
      bytes: bytes.length,
    };
  }
}

// ── printKitchen ─────────────────────────────────────────────────────────────

export async function printKitchen(
  tenantId: string,
  registerId: string | undefined,
  data: KitchenData,
  printerId?: string,
  printerFunction?: string,
): Promise<boolean> {
  const printer = await findPrinter(tenantId, registerId, 'kitchen', printerId, printerFunction);
  if (!printer) throw new Error('Aucune imprimante cuisine configurée');
  const bytes = buildEscPosKitchen(data);

  switch (printer.connection_type) {
    case 'bluetooth':
      if (!printer.bt_mac_address) throw new Error('MAC manquante');
      return sendViaBluetooth(printer.bt_mac_address, bytes, { closeAfter: true });
    case 'network':
      if (!printer.ip_address) throw new Error('IP manquante');
      return printViaNetworkOrCloud(printer.ip_address, printer.port || 9100, bytes);
    case 'sunmi': {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL non typé
      const b = (window as any).SunmiPrinter || (window as any).SunmiInnerPrinter;
      if (!b) throw new Error('Bridge AIDL non disponible');
      b.beginTransaction();
      b.setAlignment(1);
      b.setBold(true);
      b.setFontSize(28);
      if (data.reclame) b.printText('>>> RECLAME <<<'); // bandeau réclame en tête
      b.printText('*** ' + data.destination + ' ***');
      // Service de l'envoi (appel) — la cuisine distingue entrées/plats de loin.
      if (data.course) b.printText('- ' + data.course.toUpperCase() + ' -');
      if (data.table_number) b.printText('TABLE ' + data.table_number);
      b.setFontSize(22);
      b.printText('Cmd #' + data.order_number);
      b.setBold(false);
      b.setFontSize(20);
      b.printText('Serveur: ' + data.server_name);
      b.printText('================================');
      b.setAlignment(0);
      b.setFontSize(26);
      b.setBold(true);
      for (const item of data.items) {
        b.printText(item.quantity + 'x ' + item.name);
        if (item.notes) {
          b.setFontSize(22);
          b.setBold(false);
          b.printText('   >> ' + item.notes);
          b.setFontSize(26);
          b.setBold(true);
        }
      }
      b.feedLines(4);
      b.commitTransaction();
      return true;
    }
    default:
      throw new Error('Type non supporté: ' + printer.connection_type);
  }
}

// ── openDrawer ───────────────────────────────────────────────────────────────

export async function openDrawer(tenantId: string, registerId?: string): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge non typé
  const w = window as any;

  // 1) AIDL (D3/D3Mini — DLE DC4 raw bytes, seule séquence qui marche pour RJ12)
  const aidl = w.SunmiPrinter || w.SunmiInnerPrinter;
  if (aidl) {
    let aidlOk = false;
    try {
      if (typeof aidl.sendRawData === 'function') {
        aidl.sendRawData(btoa(String.fromCharCode(...DRAWER_KICK_AIDL)));
        aidlOk = true;
      }
      if (typeof aidl.openDrawer === 'function') {
        try {
          aidl.openDrawer();
          aidlOk = true;
        } catch {
          /* ignore */
        }
      }
      if (typeof aidl.openCashDrawer === 'function') {
        try {
          aidl.openCashDrawer();
          aidlOk = true;
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      console.warn('[Drawer] AIDL échec:', e);
    }
    if (aidlOk) return true;
  }

  // 2) PrinterX (D3Pro)
  const px = w.SunmiPrinterX;
  if (px) {
    try {
      if (typeof px.openCashDrawer === 'function') {
        px.openCashDrawer();
        return true;
      }
      if (typeof px.openDrawer === 'function') {
        px.openDrawer();
        return true;
      }
      if (typeof px.cashDrawerOpen === 'function') {
        px.cashDrawerOpen();
        return true;
      }
      if (typeof px.sendRawData === 'function') {
        px.sendRawData(btoa(String.fromCharCode(...DRAWER_KICK_ESCPOS)));
        return true;
      }
    } catch (e) {
      console.warn('[Drawer] PrinterX échec:', e);
    }
  }

  // 3) BT/Network : kick ESC/POS via l'imprimante receipt
  const printer = await findPrinter(tenantId, registerId, 'receipt');
  if (!printer) {
    console.warn('[Drawer] Aucune imprimante configurée pour tenant', tenantId);
    return false;
  }
  if (printer.connection_type === 'bluetooth' && printer.bt_mac_address)
    return sendViaBluetooth(printer.bt_mac_address, DRAWER_KICK_ESCPOS);
  if (printer.connection_type === 'network' && printer.ip_address)
    return printViaNetworkOrCloud(printer.ip_address, printer.port || 9100, DRAWER_KICK_ESCPOS);
  return false;
}

export async function openDrawerForRegister(
  tenantId: string,
  registerId?: string,
): Promise<boolean> {
  return openDrawer(tenantId, registerId);
}
